import React, { useState } from 'react';
import { Loader2, UserMinus } from 'lucide-react';
import { Interruptor } from './Interruptor';
import { CustomMultiSelect } from '../../components/CustomMultiSelect';
import type { MarcaDeLaCuenta } from '../../lib/equipoApi';
import type { MisPermisos } from '../../lib/permisosDelEquipo';

/**
 * Lo que el dueño le habilita a un miembro — ADR-2026-10-19-permisos-por-miembro-del-equipo.
 * Dos cosas: publicar en la tienda online y qué marcas ve. Se guarda con un botón (no al tocar)
 * porque restringir marcas le cambia la pantalla al otro, y conviene que sea a propósito.
 */

/** "Sin tienda · 2 de 3 marcas" — lo que se ve en la fila sin abrir nada. `null` = todo permitido. */
export function resumenDePermisos(p: MisPermisos, totalMarcas: number | null): string | null {
  const partes: string[] = [];
  if (!p.puedePublicarEnTienda) partes.push('Sin publicar en la tienda');
  if (p.marcas) {
    partes.push(
      totalMarcas
        ? `${p.marcas.length} de ${totalMarcas} ${totalMarcas === 1 ? 'marca' : 'marcas'}`
        : `${p.marcas.length} ${p.marcas.length === 1 ? 'marca' : 'marcas'}`,
    );
  }
  return partes.length > 0 ? partes.join(' · ') : null;
}

export const PermisosDelMiembro: React.FC<{
  nombre: string;
  inicial: MisPermisos;
  marcas: MarcaDeLaCuenta[];
  guardando: boolean;
  onGuardar: (p: MisPermisos) => void;
  onCancelar: () => void;
  /** Abre la confirmación de sacarlo del equipo (el modal lo pone `Equipo`). */
  onSacar: () => void;
}> = ({ nombre, inicial, marcas, guardando, onGuardar, onCancelar, onSacar }) => {
  const [tienda, setTienda] = useState(inicial.puedePublicarEnTienda);
  const [todas, setTodas] = useState(inicial.marcas === null);
  const [elegidas, setElegidas] = useState<Set<string>>(
    () => new Set(inicial.marcas ?? marcas.map((m) => m.id)),
  );
  // Con una sola marca no hay nada que elegir: restringirla dejaría al miembro sin marcas.
  const variasMarcas = marcas.length > 1;
  const sinMarcas = !todas && elegidas.size === 0;

  // "Todas las marcas" incluye las que se creen después; tildar una por una, no. Por eso tocar
  // una marca con "Todas" puesto pasa a "todas menos ésa", y destildar "Todas" deja tildadas
  // las de hoy para que el dueño saque las que no.
  const alternar = (id: string) => {
    if (todas) {
      setTodas(false);
      setElegidas(new Set(marcas.map((m) => m.id).filter((x) => x !== id)));
      return;
    }
    setElegidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const alternarTodas = () => {
    if (todas) {
      setTodas(false);
      setElegidas(new Set(marcas.map((m) => m.id)));
    } else {
      setTodas(true);
    }
  };
  const nombresElegidos = marcas.filter((m) => elegidas.has(m.id)).map((m) => m.nombre);
  const resumenMarcas = todas
    ? 'Todas las marcas'
    : nombresElegidos.length === 0
      ? 'Ninguna marca'
      : nombresElegidos.length <= 2
        ? nombresElegidos.join(', ')
        : `${nombresElegidos.length} de ${marcas.length} marcas`;

  const guardar = () => {
    if (sinMarcas) return;
    onGuardar({
      puedePublicarEnTienda: tienda,
      // Conserva el orden de la cuenta (la default primero) para que el resumen sea estable.
      marcas: todas || !variasMarcas ? null : marcas.filter((m) => elegidas.has(m.id)).map((m) => m.id),
    });
  };

  return (
    <div className="px-3 pb-3 pt-1 bg-white space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[14px] font-medium text-brand-black/90">Publicar en la tienda online</p>
          <p className="text-[13px] text-neutral-500">
            Subir fotos, crear productos o cambiar precios en Tiendanube y Mercado Libre.
          </p>
        </div>
        <Interruptor activo={tienda} onChange={setTienda} etiqueta={`${nombre} puede publicar en la tienda online`} />
      </div>

      {variasMarcas && (
        <div>
          <p className="text-[14px] font-medium text-brand-black/90">Marcas que ve</p>
          <p className="text-[13px] text-neutral-500 mb-2">
            Lo que es de otras marcas no le aparece: ni productos, ni creaciones, ni lanzamientos.
          </p>
          <CustomMultiSelect
            className="sm:max-w-xs"
            etiqueta={`Marcas que ve ${nombre}`}
            resumen={resumenMarcas}
            options={marcas.map((m) => ({ value: m.id, label: m.nombre }))}
            values={todas ? marcas.map((m) => m.id) : [...elegidas]}
            onToggle={alternar}
            opcionTodas={{ label: 'Todas las marcas', activa: todas, onToggle: alternarTodas }}
          />
          {sinMarcas && <p className="text-[13px] text-red-600 mt-2">Elegí al menos una marca.</p>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-neutral-200">
        <button
          type="button"
          onClick={onSacar}
          disabled={guardando}
          className="inline-flex items-center gap-1.5 py-2 px-4 rounded-md text-[14px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 mr-auto"
        >
          <UserMinus className="w-4 h-4" /> Sacar del equipo
        </button>
        <button
          type="button"
          onClick={onCancelar}
          disabled={guardando}
          className="py-2 px-4 rounded-md text-[14px] font-medium bg-white border border-neutral-200 text-brand-black/90 hover:bg-neutral-50 disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={guardar}
          disabled={guardando || sinMarcas}
          className="inline-flex items-center gap-1.5 py-2 px-4 rounded-md text-[14px] font-medium bg-brand-primary text-white hover:opacity-90 disabled:opacity-60"
        >
          {guardando && <Loader2 className="w-4 h-4 animate-spin" />}
          Guardar permisos
        </button>
      </div>
    </div>
  );
};

export default PermisosDelMiembro;
